import React, { useState } from "react";
import { useAuth } from "../../../context/AuthContext";

import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LockIcon from "@mui/icons-material/Lock";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CakeIcon from "@mui/icons-material/Cake";
import WcIcon from "@mui/icons-material/Wc";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ShieldIcon from "@mui/icons-material/Shield";

import "./styles/Settings.css";

const Section = ({
  title,
  icon,
  children,
  onSave,
  onCancel,
  editing,
  setEditing,
  noEdit,
}) => (
  <div className="st-card">
    <div className="st-card-header">
      <div className="st-card-title-row">
        <span className="st-card-icon">{icon}</span>

        <span className="st-card-title">{title}</span>
      </div>

      {!noEdit &&
        (!editing ? (
          <button
            className="st-edit-btn"
            onClick={() => setEditing(true)}
          >
            <EditIcon style={{ fontSize: 15 }} />
            Edit
          </button>
        ) : (
          <div className="st-action-row">
            <button className="st-save-btn" onClick={onSave}>
              <SaveIcon style={{ fontSize: 15 }} />
              Save
            </button>

            <button
              className="st-cancel-btn"
              onClick={onCancel}
            >
              <CloseIcon style={{ fontSize: 15 }} />
            </button>
          </div>
        ))}
    </div>

    <div className="st-card-body">{children}</div>
  </div>
);

const Field = ({
  label,
  icon,
  value,
  name,
  editing,
  onChange,
  type = "text",
  placeholder,
  locked,
  lockedNote,
  select,
  options,
}) => (
  <div className="st-field">
    <label className="st-label">
      <span className="st-label-icon">{icon}</span>

      {label}

      {locked && (
        <span className="st-locked-chip">
          <LockIcon style={{ fontSize: 11 }} />
          Locked
        </span>
      )}
    </label>

    {locked ? (
      <div className="st-value st-value--locked">
        {value || (
          <span className="st-empty">Not set</span>
        )}

        {lockedNote && (
          <span className="st-lock-note">
            {lockedNote}
          </span>
        )}
      </div>
    ) : editing ? (
      select ? (
        <select
          name={name}
          value={value}
          onChange={onChange}
          className="st-input st-select"
        >
          <option value="">Select {label}</option>

          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder || label}
          className="st-input"
        />
      )
    ) : (
      <div className="st-value">
        {value || (
          <span className="st-empty">
            Not provided
          </span>
        )}
      </div>
    )}
  </div>
);

const ToggleRow = ({
  label,
  sub,
  checked,
  onChange,
}) => (
  <div className="st-toggle-row">
    <div className="st-toggle-info">
      <div className="st-toggle-label">{label}</div>

      {sub && (
        <div className="st-toggle-sub">{sub}</div>
      )}
    </div>

    <button
      className={`st-toggle ${
        checked ? "st-toggle--on" : ""
      }`}
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
    >
      <span className="st-toggle-knob" />
    </button>
  </div>
);

const Settings = () => {
  const { user, updateUser } = useAuth();

  /* PERSONAL */
  const [editPersonal, setEditPersonal] =
    useState(false);

  const [personal, setPersonal] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    age: user?.age || "",
    gender: user?.gender || "",
    dob: user?.dob || "",
  });

  /* ADDRESS */
  const [editAddress, setEditAddress] =
    useState(false);

  const [address, setAddress] = useState({
    line1: user?.address?.line1 || "",
    line2: user?.address?.line2 || "",
    city: user?.address?.city || "",
    state: user?.address?.state || "",
    pincode: user?.address?.pincode || "",
    country: user?.address?.country || "India",
  });

  /* PASSWORD */
  const [editPassword, setEditPassword] =
    useState(false);

  const [passwords, setPasswords] = useState({
    current: "",
    newPass: "",
    confirm: "",
  });

  const [passError, setPassError] = useState("");
  const [passSuccess, setPassSuccess] =
    useState(false);

  /* NOTIFICATIONS */
  const [notifs, setNotifs] = useState({
    orderUpdates:
      user?.notifs?.orderUpdates ?? true,

    promotions:
      user?.notifs?.promotions ?? false,

    newArrivals:
      user?.notifs?.newArrivals ?? true,

    smsAlerts:
      user?.notifs?.smsAlerts ?? false,

    emailDigest:
      user?.notifs?.emailDigest ?? true,
  });

  const [savedSection, setSavedSection] =
    useState("");

  const showSaved = (section) => {
    setSavedSection(section);

    setTimeout(() => {
      setSavedSection("");
    }, 3000);
  };

  const handlePersonalChange = (e) => {
    setPersonal((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleAddressChange = (e) => {
    setAddress((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handlePasswordChange = (e) => {
    setPasswords((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const savePersonal = () => {
    updateUser(personal);

    setEditPersonal(false);

    showSaved("personal");
  };

  const cancelPersonal = () => {
    setPersonal({
      name: user?.name || "",
      phone: user?.phone || "",
      age: user?.age || "",
      gender: user?.gender || "",
      dob: user?.dob || "",
    });

    setEditPersonal(false);
  };

  const saveAddress = () => {
    updateUser({ address });

    setEditAddress(false);

    showSaved("address");
  };

  const cancelAddress = () => {
    setAddress({
      line1: user?.address?.line1 || "",
      line2: user?.address?.line2 || "",
      city: user?.address?.city || "",
      state: user?.address?.state || "",
      pincode: user?.address?.pincode || "",
      country:
        user?.address?.country || "India",
    });

    setEditAddress(false);
  };

  const savePassword = () => {
    setPassError("");

    if (!passwords.current) {
      setPassError(
        "Enter your current password."
      );
      return;
    }

    if (passwords.newPass.length < 6) {
      setPassError(
        "New password must be at least 6 characters."
      );
      return;
    }

    if (
      passwords.newPass !== passwords.confirm
    ) {
      setPassError("Passwords do not match.");
      return;
    }

    setPasswords({
      current: "",
      newPass: "",
      confirm: "",
    });

    setEditPassword(false);

    setPassSuccess(true);

    setTimeout(() => {
      setPassSuccess(false);
    }, 3500);
  };

  const cancelPassword = () => {
    setPasswords({
      current: "",
      newPass: "",
      confirm: "",
    });

    setPassError("");

    setEditPassword(false);
  };

  const saveNotifs = () => {
    updateUser({ notifs });

    showSaved("notifs");
  };

  return (
    <div className="st-root">
      {/* HEADER */}
      <div className="dash-page-header">
        <h1 className="dash-page-title">
          Settings
        </h1>

        <p className="dash-page-sub">
          Manage your account preferences and
          information
        </p>
      </div>

      <div className="st-layout">
        {/* PERSONAL */}
        <Section
          title="Personal Information"
          icon={<PersonIcon />}
          editing={editPersonal}
          setEditing={setEditPersonal}
          onSave={savePersonal}
          onCancel={cancelPersonal}
        >
          {savedSection === "personal" && (
            <div className="st-toast">
              ✓ Personal info updated
            </div>
          )}

          <div className="st-fields-grid">
            <Field
              label="Full Name"
              icon={<PersonIcon />}
              name="name"
              value={personal.name}
              editing={editPersonal}
              onChange={handlePersonalChange}
              placeholder="Your full name"
            />

            <Field
              label="Email Address"
              icon={<EmailIcon />}
              value={user?.email || ""}
              editing={false}
              locked
              lockedNote="Email cannot be changed"
            />

            <Field
              label="Phone Number"
              icon={<PhoneIcon />}
              name="phone"
              value={personal.phone}
              editing={editPersonal}
              onChange={handlePersonalChange}
              type="tel"
              placeholder="+91 XXXXX XXXXX"
            />

            <Field
              label="Date of Birth"
              icon={<CakeIcon />}
              name="dob"
              value={personal.dob}
              editing={editPersonal}
              onChange={handlePersonalChange}
              type="date"
            />

            <Field
              label="Age"
              icon={<PersonIcon />}
              name="age"
              value={personal.age}
              editing={editPersonal}
              onChange={handlePersonalChange}
              type="number"
              placeholder="Your age"
            />

            <Field
              label="Gender"
              icon={<WcIcon />}
              name="gender"
              value={personal.gender}
              editing={editPersonal}
              onChange={handlePersonalChange}
              select
              options={[
                "Male",
                "Female",
                "Non-binary",
                "Prefer not to say",
              ]}
            />
          </div>
        </Section>

        {/* ADDRESS */}
        <Section
          title="Delivery Address"
          icon={<LocationOnIcon />}
          editing={editAddress}
          setEditing={setEditAddress}
          onSave={saveAddress}
          onCancel={cancelAddress}
        >
          {savedSection === "address" && (
            <div className="st-toast">
              ✓ Address updated
            </div>
          )}

          <div className="st-fields-grid">
            <Field
              label="Address Line 1"
              icon={<LocationOnIcon />}
              name="line1"
              value={address.line1}
              editing={editAddress}
              onChange={handleAddressChange}
              placeholder="House no., Street name"
            />

            <Field
              label="Address Line 2"
              icon={<LocationOnIcon />}
              name="line2"
              value={address.line2}
              editing={editAddress}
              onChange={handleAddressChange}
              placeholder="Apartment, Area, Landmark"
            />

            <Field
              label="City"
              icon={<LocationOnIcon />}
              name="city"
              value={address.city}
              editing={editAddress}
              onChange={handleAddressChange}
              placeholder="City"
            />

            <Field
              label="State"
              icon={<LocationOnIcon />}
              name="state"
              value={address.state}
              editing={editAddress}
              onChange={handleAddressChange}
              placeholder="State"
            />

            <Field
              label="Pincode"
              icon={<LocationOnIcon />}
              name="pincode"
              value={address.pincode}
              editing={editAddress}
              onChange={handleAddressChange}
              placeholder="6-digit pincode"
            />

            <Field
              label="Country"
              icon={<LocationOnIcon />}
              name="country"
              value={address.country}
              editing={editAddress}
              onChange={handleAddressChange}
              select
              options={[
                "India",
                "Nepal",
                "Bangladesh",
                "Sri Lanka",
                "Other",
              ]}
            />
          </div>
        </Section>

        {/* PASSWORD */}
        <Section
          title="Change Password"
          icon={<LockIcon />}
          editing={editPassword}
          setEditing={setEditPassword}
          onSave={savePassword}
          onCancel={cancelPassword}
        >
          {passSuccess && (
            <div className="st-toast">
              ✓ Password changed successfully
            </div>
          )}

          {!editPassword ? (
            <div className="st-password-placeholder">
              <LockIcon className="st-pass-lock-icon" />

              <span>
                Click Edit to change your password
              </span>
            </div>
          ) : (
            <div className="st-fields-grid st-fields-grid--single">
              {passError && (
                <div className="st-error">
                  {passError}
                </div>
              )}

              <div className="st-field">
                <label className="st-label">
                  <span className="st-label-icon">
                    <LockIcon />
                  </span>
                  Current Password
                </label>

                <input
                  type="password"
                  name="current"
                  value={passwords.current}
                  onChange={handlePasswordChange}
                  placeholder="Enter current password"
                  className="st-input"
                />
              </div>

              <div className="st-field">
                <label className="st-label">
                  <span className="st-label-icon">
                    <LockIcon />
                  </span>
                  New Password
                </label>

                <input
                  type="password"
                  name="newPass"
                  value={passwords.newPass}
                  onChange={handlePasswordChange}
                  placeholder="Min. 6 characters"
                  className="st-input"
                />
              </div>

              <div className="st-field">
                <label className="st-label">
                  <span className="st-label-icon">
                    <LockIcon />
                  </span>
                  Confirm New Password
                </label>

                <input
                  type="password"
                  name="confirm"
                  value={passwords.confirm}
                  onChange={handlePasswordChange}
                  placeholder="Re-enter new password"
                  className="st-input"
                />
              </div>
            </div>
          )}
        </Section>

        {/* NOTIFICATIONS */}
        <Section
          title="Notification Preferences"
          icon={<NotificationsIcon />}
          noEdit
        >
          {savedSection === "notifs" && (
            <div className="st-toast">
              ✓ Preferences saved
            </div>
          )}

          <div className="st-toggles">
            <ToggleRow
              label="Order Updates"
              sub="Get notified about your order status changes"
              checked={notifs.orderUpdates}
              onChange={(val) =>
                setNotifs((prev) => ({
                  ...prev,
                  orderUpdates: val,
                }))
              }
            />

            <ToggleRow
              label="Promotions & Offers"
              sub="Receive deals, discount coupons and sale alerts"
              checked={notifs.promotions}
              onChange={(val) =>
                setNotifs((prev) => ({
                  ...prev,
                  promotions: val,
                }))
              }
            />

            <ToggleRow
              label="New Arrivals"
              sub="Be the first to know about new products"
              checked={notifs.newArrivals}
              onChange={(val) =>
                setNotifs((prev) => ({
                  ...prev,
                  newArrivals: val,
                }))
              }
            />

            <ToggleRow
              label="SMS Alerts"
              sub="Receive text messages on your registered phone"
              checked={notifs.smsAlerts}
              onChange={(val) =>
                setNotifs((prev) => ({
                  ...prev,
                  smsAlerts: val,
                }))
              }
            />

            <ToggleRow
              label="Weekly Email Digest"
              sub="Summary of your activity and recommendations"
              checked={notifs.emailDigest}
              onChange={(val) =>
                setNotifs((prev) => ({
                  ...prev,
                  emailDigest: val,
                }))
              }
            />
          </div>

          <button
            className="st-save-notifs-btn"
            onClick={saveNotifs}
          >
            <SaveIcon style={{ fontSize: 16 }} />
            Save Preferences
          </button>
        </Section>

        {/* SECURITY */}
        <Section
          title="Account & Security"
          icon={<ShieldIcon />}
          noEdit
        >
          <div className="st-security-rows">
            <div className="st-security-row">
              <div>
                <div className="st-security-label">
                  Account Status
                </div>

                <div className="st-security-sub">
                  Your account is active and
                  verified
                </div>
              </div>

              <span className="st-security-badge st-security-badge--active">
                Active
              </span>
            </div>

            <div className="st-security-row">
              <div>
                <div className="st-security-label">
                  Registered Email
                </div>

                <div className="st-security-sub">
                  {user?.email || "—"}
                </div>
              </div>

              <span className="st-security-badge st-security-badge--locked">
                <LockIcon style={{ fontSize: 12 }} />
                Locked
              </span>
            </div>

            <div className="st-security-row">
              <div>
                <div className="st-security-label">
                  Two-Factor Authentication
                </div>

                <div className="st-security-sub">
                  Add an extra layer of security
                  (coming soon)
                </div>
              </div>

              <span className="st-security-badge st-security-badge--soon">
                Soon
              </span>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default Settings;